import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { processAssessment } from '../api';

const Assessment = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [formData, setFormData] = useState({
    zipCode: '',
    propertyType: '',
    features: [],
    ownership: '',
    goals: [],
    email: ''
  });
  const navigate = useNavigate();

  const totalSteps = 6;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked ? [...prev[name], value] : prev[name].filter(item => item !== value)
      }));
    } else if (type === 'radio') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }
    
    setIsSubmitting(true);
    setShowLoadingScreen(true);
    
    try {
      // Transform formData to match backend expectations
      const transformedData = {
        email: formData.email,
        zipCode: formData.zipCode,
        propertyType: formData.propertyType,
        // Transform features array to lotSize
        lotSize: formData.features.includes('side-yard-backyard') ? 'large' : 
                 formData.features.includes('detached-garage') ? 'large' :
                 formData.features.includes('attached-garage') ? 'medium' : 'medium',
        // Transform goals array to primaryUse
        primaryUse: formData.goals.includes('rental-income') ? 'rental' : 
                    formData.goals.includes('family-caregiver') ? 'family' : 
                    formData.goals.includes('aging-in-place') ? 'aging' : 
                    formData.goals.includes('home-office-gym') ? 'personal' : 'other',
        timeline: '6-12 months',
        budget: 'standard',
        // Include raw form data for reference
        rawFeatures: formData.features,
        rawGoals: formData.goals,
        ownership: formData.ownership
      };
      
      const assessmentResult = await processAssessment(transformedData);
      
      // Add delay for loading animation
      await new Promise(resolve => setTimeout(resolve, 4000));
      
      // Navigate to results page with the public token
      navigate(`/assessment/${assessmentResult.publicToken}`);
    } catch (error) {
      console.error('Failed to process assessment:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to process assessment. Please try again.';
      alert(errorMessage);
      setShowLoadingScreen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressPercent = ((currentStep - 1) / (totalSteps - 1)) * 100;

  const getStepTitle = () => {
    switch(currentStep) {
      case 1: return "What is your ZIP code?";
      case 2: return "Which of these best describes your property?";
      case 3: return "What are the key characteristics of your property?";
      case 4: return "What is the ownership status of the property?";
      case 5: return "What are your main goals for building an ADU?";
      case 6: return "Where should we send your assessment?";
      default: return "ADU Feasibility Assessment";
    }
  };

  if (showLoadingScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <main className="container max-w-2xl">
          <div className="text-center mb-8">
            <div className="text-2xl font-semibold text-gray-500">ADU Lab</div>
          </div>
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-200">
            <div className="spinner w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mx-auto mb-6"></div>
            <div className="text-xl font-medium mb-4">Generating your personalized analysis...</div>
            <p className="text-gray-600 text-sm">This can take up to 60 seconds as we run real calculations.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .dash-avatar {
          flex-shrink: 0;
          width: 40px;
          height: 20px;
          position: relative;
          overflow: hidden;
          background: #1a73e8;
          border-radius: 10px;
        }
        .dash-avatar .pulse-bar {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 100%;
          height: 2px;
          color: white;
          background: linear-gradient(90deg, 
              transparent 0%, transparent 20%,
              currentColor 35%, currentColor 65%,
              transparent 80%, transparent 100%
          );
          animation: pulseMove 1.8s linear infinite;
        }
        @keyframes pulseMove {
          from { transform: translateX(-100%) translateY(-50%); }
          to { transform: translateX(100%) translateY(-50%); }
        }
        .choice-btn:has(input:checked) {
          background-color: #e8f0fe;
          border-color: #1a73e8;
          color: #1a73e8;
        }
        .form-step {
          animation: fadeIn 0.5s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>
      
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
        <main className="container max-w-2xl">
          <div className="text-center mb-8">
            <div className="text-2xl font-semibold text-gray-500" style={{fontFamily: 'Montserrat, sans-serif'}}>ADU Lab</div>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-medium mb-2">{currentStep === 1 ? 'ADU Feasibility Assessment' : getStepTitle()}</h1>
              {currentStep === 1 && <p className="text-gray-600">Answer 6 quick questions to get started.</p>}
            </div>
            
            <div className="w-full h-1 bg-gray-200 rounded-full mb-8 overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all duration-400 ease-out"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
              {/* Step 1: ZIP Code */}
              {currentStep === 1 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">What is your ZIP code?</label>
                    <input 
                      type="text" 
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                      placeholder="e.g., 90210"
                    />
                    <div className="flex gap-3 items-center bg-gray-100 rounded-xl p-4 mt-6">
                      <div className="dash-avatar">
                        <div className="pulse-bar"></div>
                      </div>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        We use the ZIP Code to analyze local zoning laws and market conditions for your potential ADU project.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Property Type */}
              {currentStep === 2 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">Which of these best describes your property?</label>
                    <div className="grid grid-cols-1 gap-3">
                      {['Single-Family', 'Two-Family', 'Three-Family', 'Multi-Family', 'Commercial', 'Other'].map((type) => (
                        <label key={type} className="choice-btn block p-4 border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
                          <input 
                            type="radio" 
                            name="propertyType" 
                            value={type.toLowerCase().replace('-', '-')}
                            onChange={handleInputChange}
                            className="hidden"
                          />
                          <span className="font-medium">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Property Features */}
              {currentStep === 3 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">What are the key characteristics of your property?</label>
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        'Detached Garage',
                        'Basement Space',
                        'Side Yard or Backyard',
                        'Attached Garage',
                        'In-law Studio',
                        'Existing - Built'
                      ].map((feature) => (
                        <label key={feature} className="choice-btn block p-4 border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
                          <input 
                            type="checkbox" 
                            name="features" 
                            value={feature.toLowerCase().replace(/ /g, '-')}
                            onChange={handleInputChange}
                            className="hidden"
                          />
                          <span className="font-medium">{feature}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-3 items-center bg-gray-100 rounded-xl p-4 mt-6">
                      <div className="dash-avatar">
                        <div className="pulse-bar"></div>
                      </div>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        This helps identify physical features that could support different, more cost-effective ADU types.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Ownership Status */}
              {currentStep === 4 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">What is the ownership status of the property?</label>
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        'Own and Occupy',
                        'Own but Do Not Occupy',
                        'Considering Buying',
                        'Other'
                      ].map((status) => (
                        <label key={status} className="choice-btn block p-4 border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
                          <input 
                            type="radio" 
                            name="ownership" 
                            value={status.toLowerCase().replace(/ /g, '-')}
                            onChange={handleInputChange}
                            className="hidden"
                          />
                          <span className="font-medium">{status}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-3 items-center bg-gray-100 rounded-xl p-4 mt-6">
                      <div className="dash-avatar">
                        <div className="pulse-bar"></div>
                      </div>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        Some localities have 'Owner-Occupancy' requirements for ADU permits, so this is an important factor.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Goals */}
              {currentStep === 5 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">What are your main goals for building an ADU?</label>
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        'Family/Caregiver',
                        'Aging in Place',
                        'Rental Income',
                        'Property Value',
                        'Legalize Unit',
                        'Other'
                      ].map((goal) => (
                        <label key={goal} className="choice-btn block p-4 border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
                          <input 
                            type="checkbox" 
                            name="goals" 
                            value={goal.toLowerCase().replace(/[\/\s]/g, '-')}
                            onChange={handleInputChange}
                            className="hidden"
                          />
                          <span className="font-medium">{goal}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: Email */}
              {currentStep === 6 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">Where should we send your ADU feasibility assessment?</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                      placeholder="your@email.com"
                    />
                    <div className="flex gap-3 items-center bg-gray-100 rounded-xl p-4 mt-6">
                      <div className="dash-avatar">
                        <div className="pulse-bar"></div>
                      </div>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        We'll send your free analysis to your inbox, as well as display it on the next screen.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200">
                <button 
                  type="button" 
                  onClick={prevStep}
                  className={`px-8 py-3 rounded-full font-medium text-gray-600 transition-all hover:bg-gray-100 ${currentStep === 1 ? 'invisible' : ''}`}
                >
                  Back
                </button>
                <button 
                  type="button" 
                  onClick={nextStep}
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-blue-600 text-white rounded-full font-medium transition-all hover:shadow-md hover:bg-blue-700"
                >
                  {currentStep === totalSteps ? 'Analyze My Property' : 'Next'}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </>
  );
};

export default Assessment;